import React from 'react';
import Icon from 'components/AppIcon';

const CompetitionSlide = () => {
  const competitors = [
    {
      name: 'International Legal Software',
      type: 'Indirect',
      strengths: ['Established brands', 'Feature-rich platforms'],
      weaknesses: ['Not Kenya-specific', 'Expensive', 'Complex setup'],
      marketShare: '15%',
      threat: 'Low'
    },
    {
      name: 'Local Legal Consultancies',
      type: 'Indirect',
      strengths: ['Local market knowledge', 'Established relationships'],
      weaknesses: ['Limited digital presence', 'Manual processes'],
      marketShare: '25%',
      threat: 'Low'
    },
    {
      name: 'Generic Practice Management',
      type: 'Indirect',
      strengths: ['General business tools', 'Cost-effective'],
      weaknesses: ['No legal specialization', 'Limited features'],
      marketShare: '20%',
      threat: 'Medium'
    }
  ];

  const competitiveAdvantages = [
    {
      icon: 'Globe',
      title: 'Kenya-Specific Design',
      description: 'Built specifically for Kenyan legal system, regulations, and court processes',
      advantage: 'Only platform designed for local market'
    },
    {
      icon: 'Users',
      title: 'Lawyer-Client Matching',
      description: 'Unique marketplace connecting clients with qualified lawyers',
      advantage: 'No direct competitor offers this feature'
    },
    {
      icon: 'Smartphone',
      title: 'Mobile-First Approach',
      description: 'Optimized for mobile devices with offline capabilities',
      advantage: 'Essential for Kenyan market penetration'
    },
    {
      icon: 'CreditCard',
      title: 'Local Payment Integration',
      description: 'M-Pesa, Airtel Money, and local banking integration',
      advantage: 'Critical for user adoption'
    },
    {
      icon: 'Languages',
      title: 'Multi-Language Support',
      description: 'English and Swahili language support',
      advantage: 'Broader market accessibility'
    },
    {
      icon: 'DollarSign',
      title: 'Affordable Pricing',
      description: 'Pricing tailored to Kenyan market conditions',
      advantage: 'Accessible to small and medium firms'
    }
  ];

  const barrierToEntry = [
    {
      icon: 'Scale',
      title: 'Legal Expertise Required',
      description: 'Deep understanding of Kenyan legal system needed'
    },
    {
      icon: 'Users',
      title: 'Network Effects',
      description: 'Value increases with more lawyers and clients on platform'
    },
    {
      icon: 'Shield',
      title: 'Regulatory Compliance',
      description: 'Strict data protection and legal compliance requirements'
    },
    {
      icon: 'Building',
      title: 'Relationships',
      description: 'Strong relationships with legal community and institutions'
    }
  ];

  const getThreatColor = (threat) => {
    const colorMap = {
      'Low': 'text-success-600 bg-success-50',
      'Medium': 'text-warning-600 bg-warning-50',
      'High': 'text-accent bg-accent-50'
    };
    return colorMap[threat] || colorMap['Low'];
  };

  return (
    <div className="py-12">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-heading font-bold text-text-primary mb-4">
            Competitive Landscape
          </h1>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Limited direct competition with significant opportunity for market leadership
          </p>
        </div>

        {/* Current Competition */}
        <div className="mb-12">
          <h2 className="text-2xl font-heading font-bold text-text-primary mb-8 text-center">
            Current Market Players
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {competitors?.map((competitor, index) => (
              <div key={index} className="bg-surface rounded-lg p-6 shadow-card border border-border">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-heading font-semibold text-text-primary">
                    {competitor?.name}
                  </h3>
                  <div className={`px-3 py-1 rounded-full text-xs font-medium ${getThreatColor(competitor?.threat)}`}>
                    {competitor?.threat} Threat
                  </div>
                </div>
                
                <div className="mb-4">
                  <span className="inline-flex items-center px-2 py-1 bg-secondary-100 text-secondary-600 rounded text-xs font-medium mb-2">
                    {competitor?.type} Competitor
                  </span>
                  <div className="text-sm text-text-secondary">
                    Market Share: {competitor?.marketShare}
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <h4 className="text-sm font-semibold text-success-600 mb-1">Strengths</h4>
                    <ul className="text-xs text-text-secondary space-y-1">
                      {competitor?.strengths?.map((strength, idx) => (
                        <li key={idx} className="flex items-center space-x-1">
                          <Icon name="Plus" size={12} className="text-success-600" />
                          <span>{strength}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-semibold text-accent mb-1">Weaknesses</h4>
                    <ul className="text-xs text-text-secondary space-y-1">
                      {competitor?.weaknesses?.map((weakness, idx) => (
                        <li key={idx} className="flex items-center space-x-1">
                          <Icon name="Minus" size={12} className="text-accent" />
                          <span>{weakness}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Competitive Advantages */}
        <div className="mb-12">
          <h2 className="text-2xl font-heading font-bold text-text-primary mb-8 text-center">
            Our Competitive Advantages
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {competitiveAdvantages?.map((advantage, index) => (
              <div key={index} className="bg-primary-50 rounded-lg p-6 border border-primary-200">
                <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center mb-4">
                  <Icon name={advantage?.icon} size={24} color="white" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-text-primary mb-2">
                  {advantage?.title}
                </h3>
                <p className="text-text-secondary text-sm mb-3">
                  {advantage?.description}
                </p>
                <div className="bg-white/70 rounded-lg p-2">
                  <div className="text-xs font-medium text-primary">
                    {advantage?.advantage}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Barriers to Entry */}
        <div className="bg-surface rounded-lg p-8 shadow-card border border-border">
          <h2 className="text-2xl font-heading font-bold text-text-primary mb-8 text-center">
            Barriers to Entry (Our Moat)
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {barrierToEntry?.map((barrier, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-secondary-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Icon name={barrier?.icon} size={32} className="text-secondary-600" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-text-primary mb-2">
                  {barrier?.title}
                </h3>
                <p className="text-text-secondary text-sm">
                  {barrier?.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Market Opportunity Summary */}
        <div className="mt-12 bg-gradient-to-r from-primary-50 to-secondary-50 rounded-lg p-8">
          <h2 className="text-2xl font-heading font-bold text-text-primary mb-6 text-center">
            Market Opportunity Summary
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-3xl font-heading font-bold text-primary mb-2">60%</div>
              <p className="text-text-secondary">Market remains unaddressed</p>
            </div>
            <div>
              <div className="text-3xl font-heading font-bold text-primary mb-2">First-Mover</div>
              <p className="text-text-secondary">Advantage in Kenya market</p>
            </div>
            <div>
              <div className="text-3xl font-heading font-bold text-primary mb-2">5+ Years</div>
              <p className="text-text-secondary">Lead time before major competition</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompetitionSlide;